import { type ReactNode } from "react"
import { IconFacebook, IconInstagram, IconTikTok, IconYouTube } from "@octopus-energy/icons"
import { externalRoutesPaths } from "@/routes/external"

const SOCIAL_ICON_SIZE = 44

type SocialPlatformConfig = {
    /** Human readable name, used to build the a11y label. */
    name: string
    /** Rendered icon. Kept in code: icons are React components, not Storyblok data. */
    icon: ReactNode
    /** Used when Storyblok has no content yet, or is unreachable. */
    fallbackHref: string
}

/**
 * Single source of truth for the social platforms supported in the footer.
 *
 * Adding a platform is a one-liner here plus a new option in the Storyblok
 * `socialLink.platform` field. No component needs to change.
 *
 * Note: display order comes from the Storyblok bloks, not from this object.
 */
export const SOCIAL_PLATFORMS = {
    youtube: {
        name: "YouTube",
        icon: (
            <IconYouTube
                size={SOCIAL_ICON_SIZE}
                color="inherit"
            />
        ),
        fallbackHref: externalRoutesPaths.social.youtube.resolve(),
    },
    instagram: {
        name: "Instagram",
        icon: (
            <IconInstagram
                size={SOCIAL_ICON_SIZE}
                color="inherit"
            />
        ),
        fallbackHref: externalRoutesPaths.social.instagram.resolve(),
    },
    facebook: {
        name: "Facebook",
        icon: (
            <IconFacebook
                size={SOCIAL_ICON_SIZE}
                color="inherit"
            />
        ),
        fallbackHref: externalRoutesPaths.social.facebook.resolve(),
    },
    tiktok: {
        name: "TikTok",
        icon: (
            <IconTikTok
                size={SOCIAL_ICON_SIZE}
                color="inherit"
            />
        ),
        fallbackHref: externalRoutesPaths.social.tiktok.resolve(),
    },
} as const satisfies Record<string, SocialPlatformConfig>

/** Derived from the registry so the type and the mapping can never drift apart. */
export type SocialPlatform = keyof typeof SOCIAL_PLATFORMS

/**
 * Storyblok may expose a platform before the matching code is deployed, so every
 * incoming value has to be checked before use.
 */
export function isSupportedSocialPlatform(platform: string): platform is SocialPlatform {
    return platform in SOCIAL_PLATFORMS
}

/** Display order of the fallback, used when Storyblok has no content. */
export const DEFAULT_SOCIAL_PLATFORMS = [
    "youtube",
    "instagram",
    "facebook",
    "tiktok",
] as const satisfies ReadonlyArray<SocialPlatform>
